import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DemoBaseCard from "@/components/DemoBaseCard";
import {
  Database, Trash2, Factory, HardDriveDownload, HardDriveUpload, AlertTriangle,
  ShieldAlert, ScrollText, RefreshCw, KeyRound, Mail, CheckCircle2,
} from "lucide-react";

const PURGE_ACTIONS = [
  { entity: "demo_data", label: "Excluir somente dados de demonstração", desc: "Remove apenas as ocorrências criadas pelo seed de demonstração, preservando registros reais." },
];

const SCOPED_LABELS = {
  occurrences: "Ocorrências",
  action_plans: "Planos de ação",
  electric_inspections: "Monitoramentos de salas elétricas",
  pga_monitorings: "Monitoramentos PGA",
  notifications: "Notificações",
};

export default function DataAdmin() {
  const [overview, setOverview] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [restorePreview, setRestorePreview] = useState(null);
  const restoreRef = useRef(null);

  const [resetStep, setResetStep] = useState(1);
  const [resetPwd, setResetPwd] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetInfo, setResetInfo] = useState(null);

  const loadOverview = useCallback(() => api.get("/data-admin/overview").then(({ data }) => setOverview(data)), []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const [tree, setTree] = useState([]);
  const [scope, setScope] = useState({ client_id: "", unit_id: "", area_id: "" });
  const [entities, setEntities] = useState(Object.keys(SCOPED_LABELS));
  const [scopedImpact, setScopedImpact] = useState(null);
  const [scopedPhrase, setScopedPhrase] = useState("");
  useEffect(() => { api.get("/hierarchy/tree").then(({ data }) => setTree(data)).catch(() => {}); }, []);

  const client = tree.find((c) => c.id === scope.client_id);
  const unit = (client?.units || []).find((u) => u.id === scope.unit_id);

  const calcScopedImpact = async () => {
    if (!scope.client_id) return toast.error("Selecione o cliente");
    try {
      const { data } = await api.get("/data-admin/scoped-impact", {
        params: { client_id: scope.client_id, unit_id: scope.unit_id || undefined, area_id: scope.area_id || undefined },
      });
      setScopedImpact(data);
      setScopedPhrase("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const executeScopedPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/scoped-purge", {
        client_id: scope.client_id, unit_id: scope.unit_id || null, area_id: scope.area_id || null,
        entities, confirm: true, phrase: scopedPhrase,
      });
      toast.success(`Exclusão concluída em ${data.scope} — ${data.total} registro(s)`);
      setScopedImpact(null);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openPurge = async (action) => {
    setPurgeTarget(action);
    setImpact(null);
    const { data } = await api.get(`/data-admin/impact/${action.entity}`);
    setImpact(data);
  };

  const executePurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/purge", { entity: purgeTarget.entity, confirm: true });
      toast.success(`Exclusão concluída — ${data.affected} registro(s) removido(s)`);
      setPurgeTarget(null);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const prepareProduction = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/prepare-production");
      toast.success(`Ambiente preparado — ${data.total} registro(s) removido(s). Estrutura preservada.`);
      setProdOpen(false);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/data-admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-astral-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup gerado e baixado");
    } catch (err) {
      toast.error("Falha ao gerar backup");
    }
  };

  const readRestoreFile = (files) => {
    const file = files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const backup = JSON.parse(reader.result);
        if (!backup.collections) throw new Error("inválido");
        setRestorePreview(backup);
      } catch (e) {
        toast.error("Arquivo de backup inválido");
      }
    };
    reader.readAsText(file);
  };

  const executeRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/restore", { confirm: true, backup: restorePreview });
      toast.success(`Restauração concluída — ${data.total} documento(s) restaurado(s)`);
      setRestorePreview(null);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const initiateReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/reset/initiate", { password: resetPwd });
      setResetInfo(data);
      setResetStep(2);
      toast.success(data.sent_via === "email"
        ? `Código enviado para ${data.email}`
        : "Código gerado — registrado no log do servidor (Resend não configurado)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data-admin/reset/confirm", { code: resetCode, phrase: resetPhrase });
      toast.success(`Reset Geral executado — ${data.total} registro(s) removido(s)`);
      setResetStep(1); setResetPwd(""); setResetCode(""); setResetPhrase(""); setResetInfo(null);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const loadLogs = () => api.get("/data-admin/system-logs?lines=200").then(({ data }) => setLogs(data));

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="data-admin-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Database size={26} className="text-primary" /> Administração de Dados
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Módulo exclusivo do Administrador Master — todas as operações são registradas na{" "}
          <Link to="/auditoria" className="text-primary hover:underline" data-testid="data-admin-audit-link">Auditoria</Link>{" "}
          e os registros de auditoria nunca são excluídos.
        </p>
      </div>

      <Tabs defaultValue="demo">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="demo" data-testid="tab-demo">Base Demonstrativa</TabsTrigger>
          <TabsTrigger value="exclusao" data-testid="tab-exclusao">Exclusão de Dados</TabsTrigger>
          <TabsTrigger value="limpeza" data-testid="tab-limpeza">Limpeza de Homologação</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">Backup e Restauração</TabsTrigger>
          <TabsTrigger value="reset" data-testid="tab-reset">Reset Geral</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs" onClick={loadLogs}>Logs do Sistema</TabsTrigger>
        </TabsList>

        <TabsContent value="demo" className="mt-6">
          <DemoBaseCard onChanged={loadOverview} />
        </TabsContent>

        <TabsContent value="exclusao" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {overview && Object.entries(overview).map(([k, v]) => (
              <Card key={k} className={`p-3.5 border-border bg-card ${k === "audit_logs" ? "border-primary/40" : ""}`} data-testid={`overview-${k}`}>
                <p className="text-[11px] text-muted-foreground leading-tight">{v.label}</p>
                <p className="font-heading text-xl font-bold mt-1">{v.count}</p>
              </Card>
            ))}
          </div>
          <div className="space-y-3">
            <Card className="p-5 border-border bg-card space-y-4" data-testid="scoped-purge-panel">
              <div>
                <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                  <ShieldAlert size={15} className="text-red-500" /> Exclusão em massa por escopo
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Exclusiva do Administrador Master. Exige seleção de <b>Cliente → Unidade → Área</b>, mostra o impacto
                  antes de confirmar, preserva o snapshot de cada registro excluído e nunca apaga a auditoria.
                  Não existe exclusão global.
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Cliente *</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                    value={scope.client_id} onChange={(e) => setScope({ client_id: e.target.value, unit_id: "", area_id: "" })}
                    data-testid="scoped-client-select">
                    <option value="">Selecione...</option>
                    {tree.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Unidade (opcional)</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm disabled:opacity-50"
                    disabled={!client} value={scope.unit_id}
                    onChange={(e) => setScope({ ...scope, unit_id: e.target.value, area_id: "" })}
                    data-testid="scoped-unit-select">
                    <option value="">Todas as unidades</option>
                    {(client?.units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Área (opcional)</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm disabled:opacity-50"
                    disabled={!unit} value={scope.area_id}
                    onChange={(e) => setScope({ ...scope, area_id: e.target.value })}
                    data-testid="scoped-area-select">
                    <option value="">Todas as áreas</option>
                    {(unit?.areas || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Tipos de registro</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {Object.entries(SCOPED_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-xs cursor-pointer rounded-md border border-border px-3 py-1.5">
                      <input type="checkbox" checked={entities.includes(k)}
                        onChange={(e) => setEntities((list) => e.target.checked ? [...list, k] : list.filter((x) => x !== k))}
                        data-testid={`scoped-entity-${k}`} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <Button variant="destructive" disabled={!scope.client_id || entities.length === 0}
                onClick={calcScopedImpact} data-testid="scoped-impact-btn">
                <AlertTriangle size={14} className="mr-2" /> Calcular impacto e confirmar
              </Button>
            </Card>

            <h3 className="font-heading font-semibold text-sm pt-2">Dados de demonstração</h3>
            {PURGE_ACTIONS.map((a) => (
              <Card key={a.entity} className="p-4 border-border bg-card flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">{a.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.desc}</p>
                </div>
                <Button variant="destructive" size="sm" onClick={() => openPurge(a)} data-testid={`purge-${a.entity}-btn`}>
                  <Trash2 size={13} className="mr-1.5" /> Excluir
                </Button>
              </Card>
            ))}
            <p className="text-xs text-muted-foreground">
              Exclusões individuais de clientes, unidades, áreas, subáreas e usuários estão disponíveis nos módulos Cadastros e Usuários — todas auditadas.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="limpeza" className="mt-6">
          <Card className="p-6 border-amber-500/40 bg-amber-500/5 space-y-4">
            <h3 className="font-heading font-semibold flex items-center gap-2 text-amber-400">
              <Factory size={17} /> Preparar Ambiente para Produção
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-red-400 mb-1.5">Será excluído:</p>
                <ul className="text-muted-foreground space-y-1 text-xs list-disc pl-4">
                  <li>Todas as ocorrências de teste (e pendências)</li>
                  <li>Monitoramentos de teste</li>
                  <li>Relatórios de teste</li>
                  <li>Notificações</li>
                  <li>Conversas da IA</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-emerald-400 mb-1.5">Será preservado:</p>
                <ul className="text-muted-foreground space-y-1 text-xs list-disc pl-4">
                  <li>Empresas, clientes, unidades, áreas e subáreas</li>
                  <li>Usuários e permissões</li>
                  <li>Configurações e parametrizações</li>
                  <li>Temas e identidade visual</li>
                  <li>Numeração de protocolos (números nunca são reutilizados)</li>
                  <li>Auditoria (imutável)</li>
                </ul>
              </div>
            </div>
            <Button variant="destructive" onClick={() => setProdOpen(true)} data-testid="prepare-production-btn">
              <Factory size={15} className="mr-2" /> Preparar Ambiente para Produção
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="backup" className="mt-6 grid sm:grid-cols-2 gap-4">
          <Card className="p-6 border-border bg-card space-y-3">
            <h3 className="font-heading font-semibold flex items-center gap-2"><HardDriveDownload size={17} className="text-primary" /> Backup</h3>
            <p className="text-sm text-muted-foreground">Exporta todos os dados do sistema (estrutura, ocorrências, usuários, configurações e temas) em um arquivo JSON.</p>
            <Button onClick={downloadBackup} data-testid="backup-download-btn">
              <HardDriveDownload size={15} className="mr-2" /> Gerar e baixar backup
            </Button>
          </Card>
          <Card className="p-6 border-border bg-card space-y-3">
            <h3 className="font-heading font-semibold flex items-center gap-2"><HardDriveUpload size={17} className="text-amber-400" /> Restauração</h3>
            <p className="text-sm text-muted-foreground">Restaura o sistema a partir de um arquivo de backup. Os dados atuais serão substituídos (auditoria é preservada).</p>
            <input ref={restoreRef} type="file" accept="application/json" hidden onChange={(e) => readRestoreFile(e.target.files)} data-testid="restore-file-input" />
            <Button variant="outline" onClick={() => restoreRef.current?.click()} data-testid="restore-select-btn">
              <HardDriveUpload size={15} className="mr-2" /> Selecionar arquivo de backup
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="reset" className="mt-6">
          <Card className="p-6 border-red-500/40 bg-red-500/5 space-y-5 max-w-xl">
            <h3 className="font-heading font-semibold flex items-center gap-2 text-red-400">
              <ShieldAlert size={17} /> Reset Geral do Sistema
            </h3>
            <p className="text-sm text-muted-foreground">
              Remove <b>todos os dados operacionais e cadastrais</b> (ocorrências, clientes, unidades, áreas, subáreas, empresas, notificações).
              Preserva usuários, configurações, temas e auditoria. Exige tripla validação: senha do Master, código enviado por e-mail e frase de confirmação.
            </p>
            {resetStep === 1 && (
              <div className="space-y-3">
                <Label className="flex items-center gap-1.5"><KeyRound size={13} /> 1ª validação — Senha do Administrador Master</Label>
                <Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Sua senha" data-testid="reset-password-input" />
                <Button variant="destructive" disabled={!resetPwd || busy} onClick={initiateReset} data-testid="reset-initiate-btn">
                  Validar senha e enviar código
                </Button>
              </div>
            )}
            {resetStep === 2 && (
              <div className="space-y-3">
                <div className="rounded-md border border-border bg-card px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
                  <Mail size={14} className="text-primary shrink-0" />
                  {resetInfo?.sent_via === "email"
                    ? <>Código enviado para <b>{resetInfo.email}</b> — válido por 10 minutos.</>
                    : <>Resend não configurado — o código foi registrado no log do servidor (válido por 10 min). Adicione RESEND_API_KEY para envio real.</>}
                </div>
                <Label>2ª validação — Código de confirmação (6 dígitos)</Label>
                <Input value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="000000" maxLength={6} className="font-mono tracking-[0.5em] text-center" data-testid="reset-code-input" />
                <Label>3ª validação — Digite exatamente: <span className="font-mono text-red-400">CONFIRMO A EXCLUSÃO DOS DADOS</span></Label>
                <Input value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder="CONFIRMO A EXCLUSÃO DOS DADOS" data-testid="reset-phrase-input" />
                <div className="flex gap-2">
                  <Button variant="destructive" disabled={resetCode.length !== 6 || resetPhrase !== "CONFIRMO A EXCLUSÃO DOS DADOS" || busy}
                    onClick={confirmReset} data-testid="reset-confirm-btn">
                    <AlertTriangle size={15} className="mr-2" /> Executar Reset Geral
                  </Button>
                  <Button variant="ghost" onClick={() => { setResetStep(1); setResetCode(""); setResetPhrase(""); }} data-testid="reset-cancel-btn">Cancelar</Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><ScrollText size={15} /> Logs do backend</h3>
            <Button variant="outline" size="sm" onClick={loadLogs} data-testid="logs-refresh-btn"><RefreshCw size={13} className="mr-1.5" /> Atualizar</Button>
          </div>
          {logs.map((f) => (
            <Card key={f.file} className="p-4 border-border bg-card">
              <p className="text-xs font-mono text-primary mb-2">{f.file}</p>
              <pre className="text-[11px] font-mono text-muted-foreground whitespace-pre-wrap max-h-80 overflow-y-auto scrollbar-thin" data-testid="system-logs-content">{f.lines.join("\n")}</pre>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <DialogContent data-testid="purge-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-400 flex items-center gap-2"><AlertTriangle size={17} /> Confirmar exclusão em massa</DialogTitle></DialogHeader>
          {purgeTarget && (
            <div className="space-y-4">
              <p className="text-sm">{purgeTarget.label}</p>
              {impact ? (
                <div className="rounded-md border border-border bg-card p-4 space-y-2 text-sm">
                  <p><b>{impact.count}</b> registro(s) serão removidos.</p>
                  {impact.dependencies.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Dependências relacionadas:</p>
                      {impact.dependencies.map((d) => (
                        <Badge key={d.label} variant="outline" className="mr-2 mb-1">{d.label}: {d.count}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : <p className="text-xs text-muted-foreground">Calculando impacto...</p>}
              <p className="text-xs text-red-400">Esta ação é irreversível e será registrada na auditoria.</p>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={executePurge} disabled={busy || !impact} data-testid="purge-execute-btn">
                  <Trash2 size={14} className="mr-2" /> {busy ? "Excluindo..." : `Excluir ${impact?.count ?? ""} registro(s)`}
                </Button>
                <Button variant="ghost" onClick={() => setPurgeTarget(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!scopedImpact} onOpenChange={(o) => !o && setScopedImpact(null)}>
        <DialogContent data-testid="scoped-purge-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-500 flex items-center gap-2"><AlertTriangle size={17} /> Confirmar exclusão por escopo</DialogTitle></DialogHeader>
          {scopedImpact && (
            <div className="space-y-4">
              <div className="rounded-md border border-border bg-card p-4 space-y-2 text-sm">
                <p className="font-medium" data-testid="scoped-impact-scope">
                  {[scopedImpact.client_name, scopedImpact.unit_name, scopedImpact.area_name].filter(Boolean).join(" › ")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {scopedImpact.entities.filter((e) => entities.includes(e.key)).map((e) => (
                    <Badge key={e.key} variant="outline" data-testid={`scoped-impact-${e.key}`}>{e.label}: {e.count}</Badge>
                  ))}
                </div>
                {scopedImpact.samples?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Exemplos de protocolos afetados: {scopedImpact.samples.slice(0, 5).join(", ")}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">{scopedImpact.audit_note}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Para confirmar, digite o nome do cliente: <span className="font-mono text-red-500">{scopedImpact.client_name}</span></Label>
                <Input value={scopedPhrase} onChange={(e) => setScopedPhrase(e.target.value)} data-testid="scoped-phrase-input" />
              </div>
              <div className="flex gap-2">
                <Button variant="destructive" disabled={busy || scopedPhrase.trim().toLowerCase() !== scopedImpact.client_name.trim().toLowerCase()}
                  onClick={executeScopedPurge} data-testid="scoped-purge-execute-btn">
                  <Trash2 size={14} className="mr-2" /> {busy ? "Excluindo..." : "Excluir registros deste escopo"}
                </Button>
                <Button variant="ghost" onClick={() => setScopedImpact(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent data-testid="production-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-amber-400 flex items-center gap-2"><Factory size={17} /> Preparar Ambiente para Produção</DialogTitle></DialogHeader>
          <p className="text-sm">Todos os dados de teste (ocorrências, monitoramentos, relatórios, notificações) serão removidos. Toda a estrutura cadastrada (clientes, unidades, áreas, usuários, configurações) será preservada.</p>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={prepareProduction} disabled={busy} data-testid="production-execute-btn">
              <CheckCircle2 size={14} className="mr-2" /> {busy ? "Executando..." : "Confirmar e preparar"}
            </Button>
            <Button variant="ghost" onClick={() => setProdOpen(false)}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restorePreview} onOpenChange={(o) => !o && setRestorePreview(null)}>
        <DialogContent data-testid="restore-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-amber-400 flex items-center gap-2"><HardDriveUpload size={17} /> Confirmar restauração</DialogTitle></DialogHeader>
          {restorePreview && (
            <div className="space-y-4">
              <div className="rounded-md border border-border bg-card p-4 text-sm space-y-1">
                <p>Backup de <b>{restorePreview.created_at?.slice(0, 16).replace("T", " ") || "?"}</b> por {restorePreview.created_by || "?"}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(restorePreview.collections).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-[10px]">{k}: {Array.isArray(v) ? v.length : 0}</Badge>
                  ))}
                </div>
              </div>
              <p className="text-xs text-red-400">Os dados atuais serão substituídos pelos do backup. A auditoria atual é preservada.</p>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={executeRestore} disabled={busy} data-testid="restore-execute-btn">
                  {busy ? "Restaurando..." : "Restaurar backup"}
                </Button>
                <Button variant="ghost" onClick={() => setRestorePreview(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
